import api from '../lib/api';

/**
 * Fetches all consultations for a user, attaches doctor info, sorts by appointmentTime,
 * and filters for upcoming (scheduled/ongoing) consultations.
 * @param {string} userId
 * @returns {Promise<Array>} Array of enriched upcoming consultations
 */
export async function fetchUpcomingConsultationsWithDoctors(userId) {
  if (!userId) return [];
  try {
    const res = await api.get(`/api/consultations/patient/${userId}`);
    const appts = Array.isArray(res?.data)
      ? res.data
      : Array.isArray(res?.data?.consultations)
        ? res.data.consultations
        : res.data || [];

    const enriched = await Promise.allSettled(
      appts.map(async (appt) => {
        const doctorId = typeof appt.doctor === 'object' ? appt.doctor?._id : appt.doctor;
        if (doctorId) {
          try {
            const doctorRes = await api.get(`/api/doctor/${doctorId}`);
            return { ...appt, doctor: doctorRes.data };
          } catch {
            // fallback to embedded doctor info
          }
        }
        return { ...appt, doctor: appt.doctor_ || appt.doctor || {} };
      })
    );

    const apptsWithDoctor = enriched
      .filter((r) => r.status === 'fulfilled')
      .map((r) => r.value);

    apptsWithDoctor.sort(
      (a, b) => new Date(a.appointmentTime) - new Date(b.appointmentTime)
    );

    return apptsWithDoctor.filter(a => ['scheduled', 'ongoing'].includes((a.status || '').toLowerCase()));
  } catch (err) {
    return [];
  }
}

/**
 * Fetches all consultations for a user, attaches doctor info, and sorts by appointmentTime (desc).
 * @param {string} userId
 * @returns {Promise<Array>} Array of all consultations (past and upcoming)
 */
export async function fetchAllConsultationsWithDoctors(userId) {
  if (!userId) return [];
  try {
    const res = await api.get(`/api/consultations/patient/${userId}`);
    const appts = Array.isArray(res?.data)
      ? res.data
      : Array.isArray(res?.data?.consultations)
        ? res.data.consultations
        : res.data || [];

    const enriched = await Promise.allSettled(
      appts.map(async (appt) => {
        const doctorId = typeof appt.doctor === 'object' ? appt.doctor?._id : appt.doctor;
        if (doctorId) {
          try {
            const doctorRes = await api.get(`/api/doctor/${doctorId}`);
            return { ...appt, doctor: doctorRes.data };
          } catch {
            // fallback to embedded doctor info
          }
        }
        return { ...appt, doctor: appt.doctor_ || appt.doctor || {} };
      })
    );

    const apptsWithDoctor = enriched
      .filter((r) => r.status === 'fulfilled')
      .map((r) => r.value);

    // Sort by appointmentTime descending (most recent first)
    apptsWithDoctor.sort((a, b) => new Date(b.appointmentTime) - new Date(a.appointmentTime));
    return apptsWithDoctor;
  } catch (err) {
    return [];
  }
}
